import { useEffect, useRef, useState } from 'react';

/**
 * Smart-polling hook for "insights still computing" surfaces.
 *
 * When `pending` is true, fires `onTick` every `intervalMs` so the
 * caller can re-fetch the cognitive / motion feeds. As soon as
 * `pending` flips false (caller's heuristic says every session has a
 * scored row), the timer stops. A hard cap (`maxAttempts`) keeps a
 * stuck worker from looping forever — the worker's exponential
 * backoff already gives up eventually, and there's no point hammering
 * the API past that point.
 *
 * Tab-visibility aware: when the operator switches away, the timer
 * pauses. Switching back resumes if `pending` is still true (and the
 * cap hasn't been hit). The next attempt fires immediately on resume
 * so the operator doesn't watch an idle page for up to intervalMs.
 *
 * The returned `polling` flag drives the "Computing insights…" chip
 * the caller renders next to its activity header.
 */
export function useInsightsPolling(opts: {
  pending: boolean;
  onTick: () => void;
  /** Polling cadence in ms. Defaults to 7 s — fast enough to feel
   *  live, slow enough that a Dashboard open for 5 minutes only
   *  costs ~40 round-trips. */
  intervalMs?: number;
  /** Max number of `onTick` invocations before the hook stops on its
   *  own. Defaults to 40 — at 7 s/each that's ~5 min of headroom,
   *  matched roughly to the worker's MAX_ATTEMPTS × backoff ceiling. */
  maxAttempts?: number;
}): { polling: boolean; attempts: number } {
  const {
    pending,
    onTick,
    intervalMs = 7_000,
    maxAttempts = 40,
  } = opts;

  // Refs so the interval callback always sees the latest values
  // without re-creating the timer every render or every tick.
  const onTickRef = useRef(onTick);
  useEffect(() => {
    onTickRef.current = onTick;
  }, [onTick]);
  const attemptsRef = useRef(0);

  const [attempts, setAttempts] = useState(0);
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    if (!pending) {
      // Reset the attempt counter when pending settles — every fresh
      // wave of unscored sessions gets a clean budget rather than
      // carrying a cap from the previous wave.
      attemptsRef.current = 0;
      setAttempts(0);
      setPolling(false);
      return;
    }

    setPolling(true);
    let timer: ReturnType<typeof setInterval> | null = null;

    const tick = () => {
      onTickRef.current();
      attemptsRef.current += 1;
      setAttempts(attemptsRef.current);
      if (attemptsRef.current >= maxAttempts) {
        stopTimer();
        setPolling(false);
      }
    };
    const startTimer = () => {
      if (timer !== null) return;
      if (attemptsRef.current >= maxAttempts) return;
      timer = setInterval(tick, intervalMs);
    };
    const stopTimer = () => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };

    const handleVisibility = () => {
      if (document.hidden) {
        stopTimer();
      } else {
        if (attemptsRef.current >= maxAttempts) return;
        // Fire immediately on resume so the operator doesn't watch
        // an idle page wait out the cadence after coming back.
        tick();
        startTimer();
      }
    };

    if (!document.hidden) {
      startTimer();
    }
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      stopTimer();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [pending, intervalMs, maxAttempts]);

  return { polling, attempts };
}
